import React, { useEffect } from "react";
import ComponentWrapper from "../components/Shared/ComponentWrapper";

const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-white">
      <ComponentWrapper styles="pt-12 sm:pt-20 pb-20">
        <div className="max-w-[800px] mx-auto">
          <h1 className="text-[36px] sm:text-[48px] font-semibold text-blue-1 leading-tight mb-2">
            Terms of Service
          </h1>
          <p className="text-[14px] text-gray-1 mb-10">
            Last updated: March 16, 2026
          </p>

          <div className="prose-custom">
            <Section title="1. Acceptance of Terms">
              <P>
                By accessing, browsing, or using the Helix AI platform
                ("Platform"), including the website located at helix.org, the
                application at ai.helix.org, and any associated services,
                application programming interfaces, or tools (collectively, the
                "Services"), you ("User", "you", or "your") acknowledge that you
                have read, understood, and agree to be bound by these Terms of
                Service ("Terms"). If you do not agree to these Terms, you must
                not access or use the Services.
              </P>
              <P>
                These Terms constitute a legally binding agreement between you
                and Helix AI ("Helix", "we", "us", or "our"). We reserve the
                right to modify these Terms at any time. Continued use of the
                Services following any changes constitutes acceptance of the
                revised Terms. It is your responsibility to review these Terms
                periodically.
              </P>
            </Section>

            <Section title="2. Description of Services">
              <P>
                Helix AI is an autonomous Ethereum AI agent that allows users
                to interact with the Ethereum network through natural
                language. The Services include, but are not limited to:
              </P>
              <Ul>
                <Li>
                  Conversational AI interface for executing blockchain
                  transactions
                </Li>
                <Li>
                  Smart contract deployment (ERC-20 tokens and custom Solidity
                  contracts)
                </Li>
                <Li>Token swaps via decentralized exchange aggregators</Li>
                <Li>Portfolio tracking on Ethereum</Li>
                <Li>Token transfers and balance inquiries</Li>
                <Li>Real-time cryptocurrency price information</Li>
                <Li>
                  Blockchain education and Helix ecosystem knowledge base
                </Li>
              </Ul>
              <P>
                The Services are provided on an "as is" and "as available" basis.
                We do not guarantee uninterrupted, error-free, or secure access
                to the Services at all times.
              </P>
            </Section>

            <Section title="3. Eligibility">
              <P>
                To use the Services, you must be at least 18 years of age or the
                age of majority in your jurisdiction, whichever is greater. By
                using the Services, you represent and warrant that:
              </P>
              <Ul>
                <Li>
                  You have the legal capacity and authority to enter into these
                  Terms.
                </Li>
                <Li>
                  You are not located in, or a resident or national of, any
                  country subject to comprehensive sanctions by the United States
                  (including but not limited to Cuba, Iran, North Korea, Syria,
                  Russia, Belarus, and the Crimea, Donetsk, and Luhansk regions
                  of Ukraine).
                </Li>
                <Li>
                  You are not listed on any U.S. government list of prohibited or
                  restricted parties, including the OFAC Specially Designated
                  Nationals and Blocked Persons List.
                </Li>
                <Li>
                  Your use of the Services does not violate any applicable law or
                  regulation in your jurisdiction.
                </Li>
              </Ul>
            </Section>

            <Section title="4. Account Registration and Wallet Connection">
              <P>
                Access to the Services requires connecting a cryptocurrency
                wallet. You may connect using supported authentication methods
                including Google, Apple, email, phone number, passkey, or an
                external wallet such as MetaMask. By connecting your wallet, you
                acknowledge and agree that:
              </P>
              <Ul>
                <Li>
                  You are solely responsible for maintaining the security of your
                  wallet credentials, private keys, and seed phrases.
                </Li>
                <Li>
                  You will not share your wallet credentials with any third
                  party.
                </Li>
                <Li>
                  Helix AI does not store, access, or control your private keys
                  at any time.
                </Li>
                <Li>
                  Any transactions initiated through your connected wallet are
                  your sole responsibility.
                </Li>
                <Li>
                  Lost or compromised wallet credentials cannot be recovered by
                  Helix AI.
                </Li>
              </Ul>
            </Section>

            <Section title="5. Credits and HLX Token">
              <H3>5.1 Credit System</H3>
              <P>
                The Services operate on a credit-based system. Each interaction
                with the AI agent consumes credits as follows:
              </P>
              <Ul>
                <Li>Standard chat messages: 1 credit per message</Li>
                <Li>ERC-20 token deployment: 100 credits per deployment</Li>
                <Li>
                  Custom Solidity contract deployment: 1,000 credits per
                  deployment
                </Li>
                <Li>
                  Token swaps, transfers, and balance checks: 1 credit
                  (standard message cost)
                </Li>
              </Ul>
              <P>
                Each user receives 10 free credits per day, replenished at
                midnight UTC. Free credits are non-transferable and expire if
                unused at the end of each day.
              </P>

              <H3>5.2 Purchasing Credits</H3>
              <P>
                Additional credits may be purchased using HLX tokens at a fixed
                rate of 1 HLX = 10 credits, up to a maximum of 10,000 credits
                per transaction. Credit purchases are final and non-refundable.
                Credits have no monetary value and cannot be exchanged, sold, or
                transferred to other users.
              </P>

              <H3>5.3 HLX Token</H3>
              <P>
                HLX is a utility token on the Ethereum blockchain (contract address:
                0x28d4e499c4cde621e1cea7c9cbf9d43bf75a9525). HLX tokens are used
                to purchase credits within the Platform. Helix AI makes no
                representations regarding the value, price stability, or
                investment potential of HLX tokens. The purchase, holding, or use
                of HLX tokens does not constitute an investment, security, or
                financial product.
              </P>
            </Section>

            <Section title="6. User Conduct">
              <P>You agree that you will not use the Services to:</P>
              <Ul>
                <Li>
                  Violate any applicable local, state, national, or
                  international law or regulation.
                </Li>
                <Li>
                  Deploy smart contracts designed to facilitate fraud, scams,
                  rug-pulls, or any malicious or deceptive activity.
                </Li>
                <Li>
                  Engage in market manipulation, wash trading, or any form of
                  artificial price manipulation.
                </Li>
                <Li>
                  Attempt to gain unauthorized access to the Services, other
                  user accounts, or underlying infrastructure.
                </Li>
                <Li>
                  Reverse engineer, decompile, disassemble, or otherwise attempt
                  to derive the source code of the Services.
                </Li>
                <Li>
                  Use automated systems, bots, or scripts to interact with the
                  Services in a manner that exceeds reasonable usage or burdens
                  the infrastructure.
                </Li>
                <Li>
                  Transmit any viruses, malware, or other harmful or disruptive
                  code.
                </Li>
                <Li>
                  Circumvent or attempt to circumvent the credit system or any
                  usage limitations.
                </Li>
                <Li>
                  Impersonate any person or entity or misrepresent your
                  affiliation with any person or entity.
                </Li>
                <Li>
                  Use the Services for money laundering, terrorist financing, or
                  sanctions evasion.
                </Li>
              </Ul>
              <P>
                We reserve the right to suspend or terminate your access to the
                Services at any time if we reasonably believe you have violated
                these Terms.
              </P>
            </Section>

            <Section title="7. Smart Contract Deployment">
              <P>
                When you deploy smart contracts through the Services, you
                acknowledge and agree that:
              </P>
              <Ul>
                <Li>
                  You are the sole deployer and owner of any smart contracts
                  deployed through your wallet.
                </Li>
                <Li>
                  Helix AI generates and compiles contract code based on your
                  instructions but does not audit, verify, or guarantee the
                  correctness, security, or fitness for purpose of any deployed
                  contract.
                </Li>
                <Li>
                  Smart contract deployments are irreversible once confirmed on
                  the blockchain.
                </Li>
                <Li>
                  You are solely responsible for testing, auditing, and verifying
                  any smart contract before and after deployment.
                </Li>
                <Li>
                  Helix AI is not liable for any financial loss, security
                  vulnerability, or unintended behavior resulting from deployed
                  smart contracts.
                </Li>
                <Li>
                  You are responsible for ensuring that deployed contracts comply
                  with all applicable laws and regulations.
                </Li>
              </Ul>
            </Section>

            <Section title="8. Third-Party Services">
              <P>
                The Services integrate with third-party protocols and services
                including, but not limited to:
              </P>
              <Ul>
                <Li>WowMax (decentralized exchange aggregation)</Li>
                <Li>CoinGecko (price data)</Li>
                <Li>Thirdweb (wallet infrastructure)</Li>
                <Li>OpenZeppelin (smart contract libraries)</Li>
                <Li>Various blockchain networks and their block explorers</Li>
              </Ul>
              <P>
                Helix AI does not control, operate, or endorse these third-party
                services. Your interactions with third-party services are subject
                to their respective terms and conditions. We are not responsible
                for the availability, accuracy, or reliability of third-party
                services, nor for any losses or damages arising from their use.
              </P>
            </Section>

            <Section title="9. Fees and Gas Costs">
              <P>
                Blockchain transactions may require payment of network fees
                ("gas") denominated in ETH. Transactions on Ethereum may be
                gas-sponsored through account abstraction, but this sponsorship
                is provided at our discretion and may be modified or discontinued
                at any time. You are solely responsible for ensuring sufficient
                funds for gas fees when sponsorship is unavailable.
              </P>
            </Section>

            <Section title="10. Disclaimers">
              <P>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
                NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
                A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              </P>
              <P>
                WITHOUT LIMITING THE FOREGOING, HELIX AI DOES NOT WARRANT THAT:
              </P>
              <Ul>
                <Li>
                  The Services will be uninterrupted, secure, or error-free.
                </Li>
                <Li>
                  The AI agent will produce accurate, complete, or reliable
                  outputs for all queries or instructions.
                </Li>
                <Li>
                  Any smart contracts generated or deployed will be free from
                  bugs, vulnerabilities, or security issues.
                </Li>
                <Li>
                  Token swaps or transfers will execute at the
                  expected price or within the expected timeframe.
                </Li>
                <Li>
                  Price data, market information, or blockchain data will be
                  accurate or current.
                </Li>
              </Ul>
              <P>
                Helix AI does not provide financial, investment, tax, or legal
                advice. Any information provided through the Services is for
                informational and educational purposes only and should not be
                relied upon as the basis for any financial decision.
              </P>
            </Section>

            <Section title="11. Limitation of Liability">
              <P>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                SHALL HELIX AI, ITS AFFILIATES, DIRECTORS, OFFICERS, EMPLOYEES,
                AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
                LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR DIGITAL ASSETS,
                ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO
                USE THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT
                (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF HELIX
                AI HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </P>
              <P>
                IN NO EVENT SHALL HELIX AI'S TOTAL LIABILITY TO YOU FOR ALL
                CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES
                EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO HELIX AI IN THE
                TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
                DOLLARS ($100).
              </P>
            </Section>

            <Section title="12. Indemnification">
              <P>
                You agree to indemnify, defend, and hold harmless Helix AI, its
                affiliates, and their respective directors, officers, employees,
                and agents from and against any and all claims, liabilities,
                damages, losses, costs, and expenses (including reasonable
                attorneys' fees) arising out of or relating to:
              </P>
              <Ul>
                <Li>Your use of the Services.</Li>
                <Li>Your violation of these Terms.</Li>
                <Li>
                  Your violation of any applicable law or regulation.
                </Li>
                <Li>
                  Any smart contracts deployed through your use of the Services.
                </Li>
                <Li>
                  Any transaction initiated through your wallet.
                </Li>
                <Li>
                  Any infringement or violation of any third party's rights.
                </Li>
              </Ul>
            </Section>

            <Section title="13. Intellectual Property">
              <P>
                The Services, including all software, algorithms, interfaces,
                designs, text, graphics, logos, and trademarks, are the
                intellectual property of Helix AI and are protected by copyright,
                trademark, and other intellectual property laws. You are granted
                a limited, non-exclusive, non-transferable, revocable license to
                access and use the Services for personal, non-commercial
                purposes in accordance with these Terms.
              </P>
              <P>
                Smart contracts that you deploy through the Services are your own
                property. Helix AI does not claim ownership over user-deployed
                contracts. However, you acknowledge that any code templates,
                libraries, or AI-generated code used in the deployment process
                may be subject to their own open-source licenses (such as the
                MIT License for OpenZeppelin contracts).
              </P>
            </Section>

            <Section title="14. Termination">
              <P>
                We may suspend or terminate your access to the Services at any
                time, with or without cause, and with or without notice. Upon
                termination, your right to use the Services will immediately
                cease. Any credits remaining in your account at the time of
                termination are forfeited and non-refundable.
              </P>
              <P>
                You may stop using the Services at any time. Sections of these
                Terms that by their nature should survive termination shall
                survive, including but not limited to Sections 10 (Disclaimers),
                11 (Limitation of Liability), 12 (Indemnification), and 13
                (Intellectual Property).
              </P>
            </Section>

            <Section title="15. Dispute Resolution">
              <P>
                Any dispute, controversy, or claim arising out of or relating to
                these Terms or the Services shall first be attempted to be
                resolved through good-faith negotiation between the parties. If
                the dispute cannot be resolved through negotiation within thirty
                (30) days, either party may pursue binding arbitration in
                accordance with the rules of the applicable arbitration
                authority in the jurisdiction of incorporation.
              </P>
              <P>
                YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE
                CONDUCTED ON AN INDIVIDUAL BASIS AND NOT IN A CLASS,
                CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU WAIVE ANY RIGHT TO
                PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
              </P>
            </Section>

            <Section title="16. Governing Law">
              <P>
                These Terms shall be governed by and construed in accordance with
                the laws of the applicable jurisdiction, without regard to its
                conflict of law principles. You agree to submit to the exclusive
                jurisdiction of the courts in the applicable jurisdiction for the
                resolution of any disputes arising under these Terms.
              </P>
            </Section>

            <Section title="17. Miscellaneous">
              <H3>17.1 Entire Agreement</H3>
              <P>
                These Terms, together with the Privacy Policy and any other
                policies referenced herein, constitute the entire agreement
                between you and Helix AI regarding the Services.
              </P>
              <H3>17.2 Severability</H3>
              <P>
                If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions shall continue in full
                force and effect.
              </P>
              <H3>17.3 Waiver</H3>
              <P>
                The failure of Helix AI to enforce any right or provision of
                these Terms shall not constitute a waiver of such right or
                provision.
              </P>
              <H3>17.4 Assignment</H3>
              <P>
                You may not assign or transfer these Terms or your rights under
                these Terms without our prior written consent. We may assign
                these Terms without restriction.
              </P>
              <H3>17.5 Force Majeure</H3>
              <P>
                Helix AI shall not be liable for any failure or delay in
                performing its obligations under these Terms due to events
                beyond its reasonable control, including but not limited to
                blockchain network congestion, smart contract exploits, natural
                disasters, pandemics, government actions, or infrastructure
                failures.
              </P>
            </Section>

            <Section title="18. Contact Information">
              <P>
                If you have questions about these Terms of Service, please
                contact us through our community channels on{" "}
                <a
                  href="https://t.me/Helix"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-2 hover:underline font-medium"
                >
                  Telegram
                </a>{" "}
                or{" "}
                <a
                  href="https://x.com/Helix"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-2 hover:underline font-medium"
                >
                  X (Twitter)
                </a>
                .
              </P>
            </Section>
          </div>
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default TermsPage;

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-[22px] sm:text-[24px] font-semibold text-blue-1 mb-4">
        {title}
      </h2>
      {children}
    </section>
  );
}

function H3({ children }) {
  return (
    <h3 className="text-[17px] font-semibold text-blue-1 mt-5 mb-2">
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p className="text-[15px] text-gray-600 leading-[26px] mb-3">{children}</p>
  );
}

function Ul({ children }) {
  return (
    <ul className="list-disc list-outside space-y-2 mb-4 pl-5">{children}</ul>
  );
}

function Li({ children }) {
  return (
    <li className="text-[15px] text-gray-600 leading-[24px]">{children}</li>
  );
}
